import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { compositionChips } from '../utils/combos';
import { useCustomerIdentity } from '../context/CustomerIdentityContext';

// Lightweight combo picker shown when a product's "Combo" button is tapped.
// Lists the real combos (promotions) and, on pick, hands the chosen combo +
// the product (as the platillo) back so the existing ComboWizard can build it.
const ComboChooser = ({ isOpen, product, combos, noDiscount = false, onPick, onClose }) => {
  const { identity, hasBenefit, benefitRemaining } = useCustomerIdentity();
  const benefitActive = !noDiscount && hasBenefit && benefitRemaining > 0;
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)' }}
          onClick={onClose}
        >
          <motion.div
            className="relative w-full max-w-md max-h-[90vh] overflow-y-auto rounded-3xl border border-white/10 bg-[#12121a]/95 backdrop-blur-xl p-5 custom-scrollbar"
            initial={{ scale: 0.92, opacity: 0, y: 30 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 30 }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start gap-3 mb-4">
              <div className="min-w-0 flex-1">
                <p className="text-amber-300 text-[11px] font-semibold uppercase tracking-wider">{noDiscount ? 'Arma tu plato con' : 'Arma tu combo con'}</p>
                <h2 className="text-white text-lg font-bold leading-tight truncate">{product?.title || 'Platillo'}</h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                className="w-9 h-9 flex-shrink-0 rounded-full grid place-items-center text-white/80 hover:text-white hover:bg-white/10 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-300/70"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-white/50 text-xs mb-3">
              {noDiscount ? 'Elige tu combinación · pagas el precio de cada porción' : 'Elige tu combinación:'}
            </p>

            {combos && combos.length > 0 ? (
              <div className="flex flex-col gap-2.5">
                {combos.map((combo) => {
                  const empPrice = identity?.benefitType === 'cortesia' ? 0 : combo.employeePrice;
                  const showEmp = benefitActive && combo.employeeBenefit &&
                    (identity?.benefitType === 'cortesia' || (empPrice != null && empPrice < combo.price));
                  return (
                  <button
                    key={combo.id}
                    type="button"
                    onClick={() => onPick(combo)}
                    className="w-full text-left rounded-2xl p-3.5 bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-400/40 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-300/70"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-white font-bold text-[15px] leading-snug">{combo.name}</span>
                      {noDiscount ? (
                        <svg className="w-5 h-5 text-white/40 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      ) : showEmp ? (
                        <span className="flex flex-col items-end flex-shrink-0 leading-none">
                          <span className="text-white/40 text-xs line-through">${combo.price}</span>
                          <span className="text-amber-300 font-black text-lg">{empPrice === 0 ? 'Gratis' : `$${empPrice}`}</span>
                        </span>
                      ) : (
                        <span className="text-amber-300 font-black text-lg leading-none flex-shrink-0">${combo.price}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {compositionChips(combo.categories).map((chip, idx) => (
                        <span key={idx} className="text-[11px] text-white/80 bg-white/5 border border-white/10 rounded-full px-2.5 py-0.5">
                          {chip}
                        </span>
                      ))}
                    </div>
                  </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-white/50 text-sm py-8 text-center">No hay combos disponibles por ahora.</p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ComboChooser;
